// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo } from 'react';
import { Pressable, StyleSheet, View, useWindowDimensions } from 'react-native';

import { Text } from '@/components/Themed';
import { getTourPhotoUri } from '@/services/tourPhotoSource';
import appColors from '@/theme/colors';

import { GRID_GAP, GRID_MAX_COLUMNS, GRID_MIN_COLUMNS, GRID_TARGET_TILE } from '../constants';
import type { TourPhoto } from '../types';
import AuthedImage from './AuthedImage';

type PhotoGridProps = {
  photos: TourPhoto[];
  apiBaseUrl: string;
  token: string | null | undefined;
  onSelect: (photo: TourPhoto) => void;
  /** Rendered above the first tile — used for the round chips and the picker bar. */
  maxWidth?: number;
};

/**
 * Square thumbnail grid, newest first.
 *
 * Plain `View` rows rather than a `FlatList`: the per-tour cap is 200 photos, and the screen already
 * scrolls as one document so the round chips and the grid scroll together. A nested FlatList inside
 * a ScrollView would fight over the scroll and warn.
 */
export default function PhotoGrid({ photos, apiBaseUrl, token, onSelect, maxWidth }: PhotoGridProps) {
  const { width: windowWidth } = useWindowDimensions();
  const available = Math.min(maxWidth ?? windowWidth, windowWidth) - 24;

  // Derive the column count from the width so tiles stay thumbnail-sized on every form factor.
  const columns = Math.max(
    GRID_MIN_COLUMNS,
    Math.min(GRID_MAX_COLUMNS, Math.round(available / GRID_TARGET_TILE)),
  );
  const tileSize = Math.floor((available - GRID_GAP * (columns - 1)) / columns);

  const rows = useMemo(() => {
    const grouped: TourPhoto[][] = [];
    for (let index = 0; index < photos.length; index += columns) {
      grouped.push(photos.slice(index, index + columns));
    }
    return grouped;
  }, [columns, photos]);

  if (photos.length === 0) {
    return null;
  }

  return (
    <View testID="tour-photo-grid">
      {rows.map((row, rowIndex) => (
        <View key={`photo-row-${rowIndex}`} style={styles.row}>
          {row.map((photo) => (
            <Pressable
              key={photo.id}
              testID={`tour-photo-tile-${photo.id}`}
              accessibilityRole="button"
              accessibilityLabel={describePhoto(photo)}
              onPress={() => onSelect(photo)}
              style={[styles.tile, { width: tileSize, height: tileSize }]}
            >
              <AuthedImage
                uri={getTourPhotoUri(apiBaseUrl, photo.tourId, photo.id, 'thumb', photo.hasThumb)}
                token={token}
                style={{ width: tileSize, height: tileSize }}
                testID={`tour-photo-image-${photo.id}`}
              />
              {photo.holeNumber !== null ? (
                <View style={styles.holeBadge} pointerEvents="none">
                  <Text style={styles.holeBadgeText}>{photo.holeNumber}</Text>
                </View>
              ) : null}
            </Pressable>
          ))}
          {/* Keeps a short final row left-aligned instead of stretching its tiles. */}
          {row.length < columns
            ? Array.from({ length: columns - row.length }).map((_, index) => (
                <View key={`filler-${rowIndex}-${index}`} style={{ width: tileSize, height: 0 }} />
              ))
            : null}
        </View>
      ))}
    </View>
  );
}

/**
 * Accessible name for a tile. A bare caption or player name reads as a mystery control in a
 * screen reader ("Anna, button"); saying what it is and where it was taken does not.
 */
const describePhoto = (photo: TourPhoto): string => {
  const parts = ['Photo'];
  if (photo.uploadedByName) {
    parts.push(`by ${photo.uploadedByName}`);
  }
  if (photo.holeNumber !== null) {
    parts.push(`on hole ${photo.holeNumber}`);
  }
  if (photo.caption) {
    parts.push(`— ${photo.caption}`);
  }
  return parts.join(' ');
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: GRID_GAP,
    marginBottom: GRID_GAP,
  },
  tile: {
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: appColors.surface.muted,
  },
  holeBadge: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    minWidth: 20,
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 999,
    backgroundColor: appColors.accent.overlay,
    borderWidth: 1,
    borderColor: appColors.accent.subtleBorder,
    alignItems: 'center',
  },
  holeBadgeText: {
    color: appColors.text.primary,
    fontSize: 11,
    fontWeight: '700',
  },
});
